import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import PullToRefresh from "@/components/PullToRefresh";
import ContactAdminDialog from "@/components/ContactAdminDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import AdminReplyDialog from "@/components/AdminReplyDialog";
import AdminComposeDialog from "@/components/AdminComposeDialog";
import { Button } from "@/components/ui/button";
import { EnvelopeIcon as Envelope, EnvelopeOpenIcon as EnvelopeOpen, TrashIcon as Trash, CheckCircleIcon as CheckCircle, PaperPlaneTiltIcon as PaperPlaneTilt, ChatCircleDotsIcon as ChatCircleDots, NotePencilIcon as NotePencil } from "@phosphor-icons/react";
import { formatDate, formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { RowListSkeleton } from "@/components/SkeletonList";

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  // Support /inbox?compose=1 deep-link from the AppShell "Contact admin" button.
  useEffect(() => {
    if (!isAdmin && searchParams.get("compose") === "1") {
      setContactOpen(true);
      searchParams.delete("compose");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  const load = () =>
    api.get("/messages").then((r) => setMessages(r.data));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await api.post(`/messages/${id}/read`).catch(() => {});
    setMessages((arr) => arr.map((m) => (m.id === id ? { ...m, read: true } : m)));
  };

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [openMsg, setOpenMsg] = useState(null);

  const onClickMessage = async (m) => {
    if (!m.read) await markRead(m.id);
    // Open the full message in a dialog — many messages link back to
    // /inbox itself, so navigating was a no-op ("View does nothing").
    setOpenMsg(m);
  };

  const markAllRead = async () => {
    try {
      await api.post("/messages/read-all");
      setMessages((arr) => arr.map((m) => ({ ...m, read: true })));
      toast.success("All marked as read");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/messages/${id}`);
      setMessages((arr) => arr.filter((m) => m.id !== id));
      setSelectedIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSelected = (id, e) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(messages.map((m) => m.id)));

  const bulkDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const { data } = await api.post("/messages/bulk-delete", { ids });
      setMessages((arr) => arr.filter((m) => !selectedIds.has(m.id)));
      clearSelection();
      toast.success(`Deleted ${data.deleted} message${data.deleted === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDeleteAllRead = async () => {
    // Confirm with the operator since this is destructive and not
    // recoverable. Browser confirm keeps the surface tiny — we don't
    // need a fancy modal for an internal-only flow.
    const readCount = messages.filter((m) => m.read).length;
    if (readCount === 0) {
      toast.info("No read messages to delete.");
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Permanently delete ${readCount} read message${readCount === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/messages/bulk-delete", { all_read: true });
      setMessages((arr) => arr.filter((m) => !m.read));
      clearSelection();
      toast.success(`Deleted ${data.deleted} read message${data.deleted === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const unread = messages.filter((m) => !m.read).length;
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    api.get("/chat/unread-count").then((r) => setChatUnread(r.data?.unread || 0)).catch(() => {});
  }, []);

  return (
    <AppShell>
      <PullToRefresh onRefresh={load} testId="inbox-ptr">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Inbox</p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Messages
            {unread > 0 && (
              <span className="ml-3 inline-flex items-center bg-[#C1272D] text-white rounded-full px-3 py-1 text-base font-semibold align-middle" data-testid="inbox-unread-badge">
                {unread} new
              </span>
            )}
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 max-w-xl">
            <Link
              to="/messages"
              className="group rounded-xl border-2 border-[#0E0E0E] bg-[#0E0E0E] text-white px-4 py-3 flex items-center gap-3 hover:bg-[#C1272D] hover:border-[#C1272D] transition-colors"
              data-testid="inbox-open-chat-link"
            >
              <span className="text-xl">💬</span>
              <span className="flex-1">
                <span className="block text-[13px] font-bold">Direct messages</span>
                <span className="block text-[11px] opacity-75">Chat with any member — breeders, buyers, pros</span>
              </span>
              {chatUnread > 0 && (
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#C9A227] text-[#0E0E0E] text-[11px] font-bold" data-testid="inbox-chat-unread-badge">
                  {chatUnread}
                </span>
              )}
              <span className="opacity-60 group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
            <Link
              to="/inbox/threads"
              className="group rounded-xl border-2 border-[#E5D08A] bg-[#FBF7EC] px-4 py-3 flex items-center gap-3 hover:border-[#C9A227] transition-colors"
              data-testid="inbox-open-conversations-link"
            >
              <span className="text-xl">🤝</span>
              <span className="flex-1">
                <span className="block text-[13px] font-bold text-ink">Providers &amp; clients</span>
                <span className="block text-[11px] text-[#8A6A00]">Booking conversations with your pros</span>
              </span>
              <span className="text-[#8A6A00] group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
          </div>
        </div>
        {unread > 0 && (
          <Button
            onClick={markAllRead}
            variant="outline"
            className="rounded-full border-[#E5E5E5]"
            data-testid="mark-all-read-btn"
          >
            <CheckCircle size={16} className="mr-2" />
            Mark all as read
          </Button>
        )}
        {!isAdmin && (
          <Button
            onClick={() => setContactOpen(true)}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="inbox-contact-admin-btn"
          >
            <PaperPlaneTilt size={16} className="mr-2" weight="duotone" />
            Contact admin
          </Button>
        )}
        {isAdmin && (
          <Button
            onClick={() => setComposeOpen(true)}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="inbox-admin-compose-btn"
          >
            <NotePencil size={16} className="mr-2" weight="duotone" />
            Compose message
          </Button>
        )}
      </div>

      {/* Bulk-action bar — appears when any messages are selected,
          plus a persistent "Delete all read" button for admins (admin
          inboxes get noisy with system + contact-admin notifications). */}
      {!loading && messages.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-[12px]" data-testid="inbox-bulk-bar">
          {selectedIds.size > 0 ? (
            <>
              <span className="font-semibold text-ink mr-1">
                {selectedIds.size} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={selectAllVisible}
                className="rounded-full h-8 px-3 text-[12px]"
                disabled={selectedIds.size === messages.length}
                data-testid="inbox-bulk-select-all"
              >
                Select all ({messages.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={clearSelection}
                className="rounded-full h-8 px-3 text-[12px]"
                data-testid="inbox-bulk-clear"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={bulkDeleteSelected}
                disabled={bulkBusy}
                className="rounded-full bg-[#C1272D] hover:bg-[#9F2125] text-white h-8 px-3 text-[12px]"
                data-testid="inbox-bulk-delete"
              >
                <Trash size={12} className="mr-1" />
                {bulkBusy ? "Deleting…" : `Delete ${selectedIds.size}`}
              </Button>
            </>
          ) : (
            <>
              <span className="text-[#6B6B6B] mr-2">
                Tip: tap the checkbox to select, then bulk-delete.
              </span>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={bulkDeleteAllRead}
                  disabled={bulkBusy}
                  className="rounded-full h-8 px-3 text-[12px] border-[#C1272D]/30 text-[#C1272D] hover:bg-[#FEE2E2]"
                  data-testid="inbox-bulk-delete-read"
                >
                  <Trash size={12} className="mr-1" />
                  Delete all read
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {loading ? (
        <RowListSkeleton count={5} testId="inbox-loading-skeleton" />
      ) : messages.length === 0 ? (
        <EmptyState
          testId="inbox-empty"
          icon={<Envelope size={32} weight="duotone" />}
          title="No messages"
          description="Confirmations, transfer updates, breeding interest, and admin notes will appear here. You can also message support directly."
          ctaLabel={isAdmin ? "Compose message" : "Contact admin"}
          ctaOnClick={() => (isAdmin ? setComposeOpen(true) : setContactOpen(true))}
          secondaryLabel="View dashboard"
          secondaryTo="/dashboard"
        />
      ) : (
        <div className="space-y-2">
          {messages.map((m) => (
            <div
              key={m.id}
              onClick={() => onClickMessage(m)}
              className={`group cursor-pointer flex items-start gap-4 p-5 rounded-xl border transition-all active:scale-[0.99] ${
                selectedIds.has(m.id)
                  ? "bg-[#FBF7EC] border-[#C9A227]/60 shadow-[0_4px_20px_rgba(193,162,39,0.18)]"
                  : m.read
                  ? "bg-white border-[#E5E5E5] hover:bg-[#FAFAFA]"
                  : "bg-white border-[#0E0E0E]/30 shadow-[0_8px_30px_rgba(0,0,0,0.05)] relative before:absolute before:left-0 before:top-3 before:bottom-3 before:w-0.5 before:bg-gradient-to-b before:from-[#C9A227] before:to-[#C1272D] before:rounded-r"
              }`}
              data-testid={`message-${m.id}`}
            >
              {/* Selection checkbox — always visible so bulk-select is
                  discoverable. Lives outside the row click target via
                  stopPropagation. */}
              <label
                className="flex-shrink-0 mt-1 inline-flex items-center justify-center h-5 w-5 rounded border border-[#D4D4D4] bg-white hover:border-ink cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(m.id)}
                  onChange={(e) => toggleSelected(m.id, e)}
                  className="absolute opacity-0 h-0 w-0"
                  data-testid={`select-message-${m.id}`}
                  aria-label={`Select message: ${m.title}`}
                />
                {selectedIds.has(m.id) && (
                  <CheckCircle size={18} weight="fill" className="text-[#C9A227]" />
                )}
              </label>
              <div className={`p-2 rounded-lg flex-shrink-0 ${m.read ? "bg-[#FAFAFA]" : "bg-[#C1272D]/10"}`}>
                {m.read ? (
                  <EnvelopeOpen size={22} className="text-[#6B6B6B]" />
                ) : (
                  <Envelope size={22} weight="fill" className="text-crimson" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className={`font-spectral text-lg tracking-tight truncate ${!m.read && "font-semibold"}`}>
                    {m.title}
                  </h3>
                  <span className="text-xs text-[#6B6B6B] flex-shrink-0">{formatDate(m.created_at)}</span>
                </div>
                <p className="text-sm text-[#6B6B6B] whitespace-pre-wrap line-clamp-3">{m.body}</p>
                {m.kind === "user_contact" && m.metadata?.from_user_email && (
                  <p className="text-[11px] text-[#6B6B6B] mt-1">
                    From <span className="font-mono">{m.metadata.from_user_email}</span>
                    {m.metadata.replied_at && (
                      <span className="ml-2 text-ink font-semibold">
                        · Replied {formatDate(m.metadata.replied_at)}
                      </span>
                    )}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClickMessage(m); }}
                    className="text-xs text-ink hover:underline font-semibold"
                    data-testid={`inbox-view-${m.id}`}
                  >
                    View →
                  </button>
                  {isAdmin && m.kind === "user_contact" && !m.metadata?.replied_at && (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setReplyTarget(m);
                      }}
                      className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-7 px-3 text-xs"
                      data-testid={`reply-message-${m.id}`}
                    >
                      <ChatCircleDots size={12} className="mr-1" weight="duotone" />
                      Reply
                    </Button>
                  )}
                </div>
              </div>
              <button
                onClick={(e) => onDelete(m.id, e)}
                className="opacity-0 group-hover:opacity-100 text-crimson hover:opacity-70 transition-opacity flex-shrink-0"
                data-testid={`delete-message-${m.id}`}
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ContactAdminDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        onSent={load}
      />

      {/* Full-message reader */}
      <Dialog open={!!openMsg} onOpenChange={(v) => !v && setOpenMsg(null)}>
        <DialogContent className="max-w-lg" data-testid="inbox-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-spectral tracking-tight pr-6">{openMsg?.title}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#6B6B6B]">{openMsg && formatDate(openMsg.created_at)}</p>
          <div className="text-sm text-[#3F3F3F] whitespace-pre-wrap leading-relaxed max-h-[50vh] overflow-y-auto" data-testid="inbox-message-body">
            {openMsg?.body}
          </div>
          {openMsg?.kind === "user_contact" && openMsg?.metadata?.from_user_email && (
            <p className="text-[11px] text-[#6B6B6B]">
              From <span className="font-mono">{openMsg.metadata.from_user_email}</span>
            </p>
          )}
          <DialogFooter className="gap-2">
            {openMsg?.link && openMsg.link !== "/inbox" && (
              <Button
                onClick={() => { const l = openMsg.link; setOpenMsg(null); navigate(l); }}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="inbox-message-open-link"
              >
                Open related page →
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setOpenMsg(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AdminReplyDialog
        open={Boolean(replyTarget)}
        onClose={() => setReplyTarget(null)}
        message={replyTarget}
        onReplied={load}
      />
      <AdminComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={load}
      />
      </PullToRefresh>
    </AppShell>
  );
}
